import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { STATUS, money } from "@/components/obboard/BoardFilters";
import { fmtAmount, fmtDate } from "@/lib/format";

export const monthName = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
const th = "px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-medium";

const Row = ({ r, months }) => {
  const [open, setOpen] = useState(false);
  const label = r.entity === "—" ? "No entity" : r.entity;
  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => setOpen(!open)} data-testid={`ob-fc-row-${r.entity}`}>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-1.5 text-sm text-slate-900">{open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}{label}</div>
          {r.no_amount > 0 && <div className="text-[11px] text-amber-700 pl-5">{r.no_amount} payment(s) without amount</div>}
        </td>
        {r.months.map((m, i) => (
          <td key={i} className="px-3 py-2.5 text-right">
            <div className="mono text-sm text-blue-800 font-semibold">{money(m.amount)}</div>
            <div className="text-[11px] text-slate-500">{m.count} payment(s){Object.keys(m.auto).length > 0 && <> · auto {money(m.auto, true)}</>}{i === 0 && Object.keys(r.overdue).length > 0 && <span className="text-rose-700"> · incl. overdue {money(r.overdue, true)}</span>}</div>
          </td>
        ))}
        <td className="px-3 py-2.5 text-right mono text-sm font-semibold text-slate-900">{money(r.total)}</td>
      </tr>
      {open && (
        <tr className="bg-slate-50/60" data-testid={`ob-fc-items-${r.entity}`}>
          <td colSpan={months.length + 2} className="px-8 py-2">
            <table className="w-full text-xs">
              <tbody>
                {r.items.map((it, k) => (
                  <tr key={k} className="border-b border-slate-100 last:border-0">
                    <td className="py-1.5 mono text-slate-600 w-28">{fmtDate(it.due_date)}</td>
                    <td className="py-1.5 text-slate-900">{it.name}<span className="text-slate-400"> · {it.category}</span></td>
                    <td className="py-1.5 w-24"><span className={`px-1.5 py-0.5 rounded-sm border text-[10px] ${STATUS[it.status].cls}`}>{STATUS[it.status].label}</span></td>
                    <td className="py-1.5 w-20 text-slate-500">{it.payment_mode === "auto" ? "Auto debit" : "Manual"}</td>
                    <td className="py-1.5 w-32 text-right mono text-slate-900">{it.amount != null ? fmtAmount(it.amount, it.currency) : "varies"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
};

export const Forecast = ({ data }) => {
  const grand = data.totals.reduce((a, t) => { Object.entries(t.amount).forEach(([c, v]) => { a[c] = (a[c] || 0) + v; }); return a; }, {});
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="ob-forecast">
      <div className="px-4 pt-4 pb-2">
        <div className="text-sm font-semibold text-slate-900">Cash-flow forecast · funds needed per entity</div>
        <div className="text-[11px] text-slate-500">Unpaid amounts due in each month. Overdue amounts from earlier months are added to the first month.</div>
      </div>
      <table className="w-full min-w-[760px] text-sm">
        <thead className="bg-slate-50 border-y border-slate-200"><tr>
          <th className={`${th} text-left`}>Entity / client</th>
          {data.months.map((m, i) => <th key={m} className={`${th} text-right`} data-testid={`ob-fc-month-${i}`}>{monthName(m)}{i === 0 ? " (incl. overdue)" : ""}</th>)}
          <th className={`${th} text-right`}>Total</th>
        </tr></thead>
        <tbody>
          {data.rows.map((r) => <Row key={r.entity} r={r} months={data.months} />)}
          {data.rows.length === 0 && <tr><td colSpan={data.months.length + 2} className="py-10 text-center text-slate-500" data-testid="ob-fc-empty">Nothing to pay in the next {data.months.length} months.</td></tr>}
        </tbody>
        {data.rows.length > 0 && (
          <tfoot><tr className="border-t border-slate-200 bg-slate-50/60" data-testid="ob-fc-totals">
            <td className="px-3 py-2.5 text-xs font-semibold text-slate-900">All entities</td>
            {data.totals.map((t, i) => <td key={i} className="px-3 py-2.5 text-right mono text-sm font-semibold text-blue-800" data-testid={`ob-fc-total-${i}`}>{money(t.amount)}</td>)}
            <td className="px-3 py-2.5 text-right mono text-sm font-semibold text-slate-900">{money(grand)}</td>
          </tr></tfoot>
        )}
      </table>
    </div>
  );
};
